/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect
} from 'react';
import css from '@emotion/css';
import ReactDOM from 'react-dom';
import {
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Tooltip
} from '@material-ui/core';
import styled from 'styled-components/macro';
import {
  MULTISET,
  MULTI_UPDATE_FIELD_ACCESS,
  REORDER_FIELDS,
  UPDATE_FORM
} from '../../store/modules/state/formBuilder';
import { useSelector } from 'react-redux';
import './formbuilder.css';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PreviewForm from './PreviewForm';
import MuiCreateIcon from '@material-ui/icons/Create';
import {
  WindowScroller,
  CellMeasurer,
  CellMeasurerCache,
  List
} from 'react-virtualized';

import FormBuilderFieldForm, {
  AddQuestionButton
} from './FormBuilderFieldForm';
import useFetchWithCallbacks from '../../hooks/useFetchWithCallbacks';

import debounce from 'lodash/debounce';
import { getAllFields } from './getAllFields';
import FormPreviewEngine from '../../components/engine/FormPreviewEngine';
import Colors from '../Colors';
import EditableTitle from '../EditableTitle';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../FetchCallbacks';
import { FormBuilderContext } from '../../components/engine/FormBuilderEngine';
import EmojiInserterEngine from '../emoji-inserter/EmojiInserterEngine';
import CharacterCounter from '../CharacterCounter';
import Animation from '../../components/Animation';
import InputBase from '../../components/form/InputBase';
import Scroll2TopButton from '../../components/Scroll2TopButton';
import FormBuilderMultiControlsEngine from './engine/FormBuilderMultiControlsEngine';
import ReorderSectionsDialogEngine from './engine/ReorderSectionsDialogEngine';
import getLicense from '../../utils/getLicense';
import { isEmpty } from '../../utils/isObjecEmpty';
gsap.registerPlugin(ScrollToPlugin);

const SplitPane = styled.div`
  display: flex;
  max-height: 100%;

  max-width: 1200px;
  min-width: 1200px;
  margin: 0 auto;

  .ReactVirtualized__Grid {
    outline: none !important;
  }
`;

const Pane = styled.div.attrs(({ width }) => ({ style: { width } }))`
  min-height: 100%;
  flex: none;
  padding: 20px;
  p {
    font-size: 14px;
  }
`;

const PreviewWrapper = styled.div`
  background: #fff;
  min-height: 100%;
  text-align: left;
`;

const Container = styled.div`
  width: 100%;
  overflow-x: hidden;

  h1 {
    background: ${Colors.BACKGROUND_GREY};
    z-index: 9;
    padding: 0 20px 2.3rem;
    position: sticky;
    top: 0;
  }

  @media print {
    && {
      overflow: initial;
      overflow-x: visible;
      position: static;
      width: 100%;
      max-width: 90vw;
      margin: 0 auto;
    }

    * {
      box-shadow: none !important;
    }
  }
`;

const BuilderTitle = styled.div`
  text-align: left;
  max-width: 1200px;
  margin: 0 auto;
  padding: 22px;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${Colors.BACKGROUND_GREY};
  ${({ $useOldFB }) => $useOldFB && 'position: initial !important;'}
`;

const EmojiIcon = styled.span`
  &::before {
    content: '☺';
    font-size: 28px;
    position: relative;
    bottom: 3px;
  }
`;
const CreateIcon = styled(MuiCreateIcon)`
  && {
    width: 22px;
    height: 22px;
  }
`;

const TitleButtonsDiv = styled.div`
  position: absolute;
  right: 1rem;
`;
const Scroll2TopDiv = styled.div`
  text-align: center;
  padding: 0.8rem 0 0;
`;

const ReorderSectionsLinkContainer = styled.div`
  text-align: left;
  max-width: 1200px;
  position: absolute;
  top: 88px;
`;

const ReorderSectionsLink = styled.span`
  color: ${Colors.LINKS};
  cursor: pointer;
`;

const SelectAllLink = styled.span`
  color: ${Colors.LINKS};
  cursor: pointer;
`;

export const FormBuilderEditContext = React.createContext({});

function Item({ provided, snapshot, isLast, index }) {
  const context = React.useContext(FormBuilderEditContext);
  const field = context.allFields[index];

  return (
    <div
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      ref={provided.innerRef}
      data-rv-index={index}
    >
      <FormBuilderFieldForm
        {...field}
        {...context}
        field={field}
        index={index}
        provided={provided}
        snapshot={snapshot}
        isLast={isLast}
        isNew={context.oldFields.every(
          ogField => ogField.internalId !== field.internalId
        )}
        anyFieldSelected={context.anyFieldSelected}
      />
    </div>
  );
}

const getRowRender =
  (
    allFields,
    hasEntries,
    measureCache,
    listOfNewFieldIdsRef,
    setShowMultiControls,
    setCanChangeAllToAdmin
  ) =>
  props => {
    const { index } = props;
    const internalId = allFields[index].internalId;
    const isLast = index === allFields.length - 1;

    return (
      <Row
        {...props}
        internalId={internalId}
        isLast={isLast}
        measureCache={measureCache}
      />
    );
  };

const Row = React.memo(
  ({ index, style, internalId, isLast, measureCache, ...rest }) => {
    return (
      <Draggable draggableId={internalId} index={index} key={internalId}>
        {(provided, snapshot) => (
          <CellMeasurer
            {...rest}
            rowIndex={index}
            columnIndex={0}
            cache={measureCache}
          >
            {({ registerChild }) => (
              <div
                ref={registerChild}
                style={style}
                id={`react-virtualized-row-${index}`}
              >
                <Item
                  provided={provided}
                  snapshot={snapshot}
                  index={index}
                  isLast={isLast}
                />
              </div>
            )}
          </CellMeasurer>
        )}
      </Draggable>
    );
  }
);

function clamp(num, min, max) {
  return num <= min ? min : num >= max ? max : num;
}

const recomputeVLRefHeights = (index, r) => {
  if (r && r.recomputeRowHeights) {
    r.recomputeRowHeights(index);
  }
};

const debouncedRecomputeVLRefHeights = debounce(recomputeVLRefHeights, 500);

const FormBuilderEdit = ({
  form,
  formId,
  rules,
  setRules,
  reminders,
  setReminders,
  allLookupDependencies,
  autosave,
  hasEntries,
  setIsOverviewDialogOpen
}) => {
  const jwt = getJWT();
  const titleRef = useRef(null);
  const fields = useSelector(state =>
    state.state.formBuilder[formId]
      ? state.state.formBuilder[formId].fields
      : []
  );
  const showReorderSectionsLink =
    fields.filter(one => one.uiType === 'section_break').length > 1;

  const leftPanePercent = 50;
  const assets = useSelector(state => state.content.assets);
  const fetchAPI = useFetchWithCallbacks();
  const t = useTranslations();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { reduxDispatch, updateData } = useContext(FormBuilderContext);
  const [deletedField, setDeletedField] = useState('');
  const [showEmojiDialog, setShowEmojiDialog] = useState(false);
  const [isReorderSectionsDialogOpen, setIsReorderSectionsDialogOpen] =
    useState(false);
  const [isTitleInFocus, setIsTitleInFocus] = useState(false);
  const [isTitleHovered, setIsTitleHovered] = useState(false);
  const [isScroll2TopBtnHidden, setIsScroll2TopBtnHidden] = useState(true);
  const adminFields = form.adminFields || [];
  const emptyState = (
    <div
      css={css`
        text-align: center;
      `}
    >
      <img src={assets['form-builder/build-form.png']} alt="" />
      <br />
      <br />
      Nothing to see yet.
      <br />
      <br />
      Start building!
    </div>
  );

  const instructions = form && form.instructions;

  const [title, setTitle] = useState(form && form.title);

  const [showPreview, setShowPreview] = React.useState(false);

  useEffect(() => {
    if (form.title !== title) {
      setTitle(form.title);
    }
  }, [form.title]);

  const toggleSelectAllFields = () => {
    const fieldCount = allFields.length;
    const numberCurrentlyMultiSelected = Object.values(multiSelected).filter(
      one => one
    ).length;
    if (numberCurrentlyMultiSelected === fieldCount) {
      // deselecting all
      setMultiSelected({});
    } else {
      const allSelectedAreNew =
        listOfNewFieldIdsRef.current.length === fieldCount;
      let newMultiSelected = {};
      allFields.forEach(one => {
        newMultiSelected['formbuilder-field-' + one.internalId] = true;
      });
      setMultiSelected(newMultiSelected);
      setCanChangeAllToAdmin(!hasEntries || allSelectedAreNew);
    }
  };

  const updateFormTitle = useCallback(
    v => {
      const newTtitle = (v || title || '').trim();

      if (newTtitle === '') {
        setTitle(form.title);
        openSnackbar(
          'error',
          t('import-form.steps.upload.snackbar.fail.emptyTitle')
        );
        return;
      }

      if (form.title === newTtitle) {
        return;
      }
      fetchAPI(`/forms/v1/forms/${formId}`, {
        method: 'PUT',
        body: { ...form, title: newTtitle },
        handleResponse: r =>
          r
            .text()
            .then(text => (text ? JSON.parse(text) : {}))
            .then(json => {
              if (r.ok) {
                return r;
              } else {
                return Promise.reject({
                  response: r,
                  json
                });
              }
            }),
        handleValidationError: data => {
          const error = data.errors ? data.errors[0] : {};
          const { code, message } = error;

          if (code === '23505') {
            openSnackbar('error', t('form-builder.overview.title_exists'));
          } else {
            openSnackbar('error', `Invalid: ${message}`);
          }
        }
      }).then(() => {
        // update FormBuilder state:
        // updateData({
        //   type: 'set_form',
        //   payload: { ...form, title: newTtitle }
        // });
        reduxDispatch({
          type: UPDATE_FORM,
          payload: {
            id: formId,
            ...{ ...form, title: newTtitle }
          }
        });
        openSnackbar('success', 'Your form has updated.');
      });
    },
    [title, form]
  );

  React.useEffect(() => {
    if (!showPreview) {
      setTimeout(() => {
        setShowPreview(true);
      }, 100);
    }
  }, [showPreview, showEmojiDialog]);

  let allFieldsWithSubFields = getAllFields(form)
    .filter(f => !f.archived)
    .sort((a, b) => a.position - b.position);

  // merge and sort, skipping archived fields and subfields
  let allFields = allFieldsWithSubFields.filter(
    f =>
      !f.lookupInfo ||
      !f.lookupInfo.subFieldInfo ||
      !f.lookupInfo.subFieldInfo.parentFieldId
  );

  const useOldFB = allFields.length >= 1000;

  // create map of access for each field by its ID
  const accessMap = {};

  allFields.forEach(f => {
    const index = adminFields.findIndex(af => f.internalId === af.internalId);
    accessMap[f.internalId] = index >= 0 ? 'admin' : 'user';
  });

  const [, setFieldsInUse] = useState({});
  const [activeIntegrations, setActiveIntegrations] = useState({});
  const [activeLicenses, setActiveLicenses] = useState({}); // can add more license checks later
  const [showMultiControls, setShowMultiControls] = useState(0);
  const [canChangeAllToAdmin, setCanChangeAllToAdmin] = useState(true);
  const [selectedAreAllAdmin, setSelectedAreAllAdmin] = useState(false);
  React.useEffect(() => {
    const allPromises = [
      fetchAPI(
        `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"}]]`
      ),
      getLicense('currencyconversion')
    ];
    Promise.all(allPromises).then(responses => {
      const integrationRes = responses[0];
      const currencyLicense = responses[1];
      let fsInUse, newActiveIntegrations, integrationResults;
      fsInUse = {};
      newActiveIntegrations = {};
      integrationResults = integrationRes.results;
      integrationResults.forEach(integration => {
        if (integration.mapping && integration.mapping.fields) {
          integration.mapping.fields.forEach(field => {
            fsInUse[field.code] = integration.name;
          });
        }
        if (integration.integrationType) {
          newActiveIntegrations[integration.integrationType] = true;
        }
      });
      if (
        isEmpty(currencyLicense) ||
        !currencyLicense.value ||
        currencyLicense.value === '0'
      ) {
        setActiveLicenses({});
      } else {
        setActiveLicenses({ currencyconversion: true });
      }
      setFieldsInUse(fsInUse);
      setActiveIntegrations(newActiveIntegrations);
    });
  }, [fetchAPI]);

  // Discover new fields by comparing with a ref
  const allFieldsRef = React.useRef(allFields);
  const listOfNewFieldIdsRef = React.useRef([]);

  if (!allFieldsRef.current) {
    allFieldsRef.current = allFields;
  }

  useEffect(() => {
    if (!allFieldsRef.current || !allFields) {
      return;
    }
    const oldFieldIds = allFieldsRef.current.map(one => one.internalId);
    const currentFieldIds = allFields.map(one => one.internalId);
    const difference = currentFieldIds.filter(x => !oldFieldIds.includes(x));
    if (difference.length) {
      listOfNewFieldIdsRef.current = difference;
    }
  }, [allFields]);

  const containerRef = React.useRef();
  const pane1Ref = React.useRef();
  const pane2Ref = React.useRef();
  const trackRef = React.useRef();
  const trainRef = React.useRef();
  const previewListRef = React.useRef();

  const container = containerRef.current;

  const topGap = 240;

  const [previewHeight, setPreviewHeight] = React.useState(window.innerHeight);

  React.useEffect(() => {
    const updateHeight = e => {
      setPreviewHeight(window.innerHeight);
    };

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // console.log({ previewHeight });

  window.previewListRef = previewListRef;

  const lastSyncRef = React.useRef({ y: 0, animating: false });

  const syncPreview = (builderField, offset = 0) => {
    const fieldId = builderField.getAttribute('data-rbd-draggable-id');

    if (allFields.length < 1500) {
      gsap.to('[id=preview-pane]', {
        scrollTo: { y: `[field-id="${fieldId}"]`, offsetY: offset }
      });
      return;
    }

    try {
      lastSyncRef.current.animating = true;

      let y;

      trainRef.current.style.transition = `transform 0s`;

      const i = +builderField.getAttribute('data-rv-index');

      // console.log('scrollto', i);

      const previewEls = pane2Ref.current.querySelectorAll(
        '[class*="PreviewForm__HalfFade"]'
      );

      const jumpingEl = previewEls[Math.min(8, previewEls.length - 1)];

      const jumpStart = jumpingEl.getBoundingClientRect().top;
      trainRef.current.style.transform = `translateY(0px)`;

      // scroll the preview, instant jump
      if (previewListRef.current) {
        previewListRef.current.scrollToRow(i);
      }

      let newGap = containerRef.current.scrollTop < topGap ? topGap : 0;

      trackRef.current.style.top = newGap + 'px';

      const id = builderField.getAttribute('data-rbd-draggable-id');

      const previewField = pane2Ref.current.querySelector(
        `[data-testid=enduserform-field-${id}]`
      );

      y =
        builderField.getBoundingClientRect().top -
        previewField.getBoundingClientRect().top;

      if (containerRef.current.scrollTop < 50) {
        y = 0;
      }

      trainRef.current.style.transform = `translateY(${y}px)`;

      const jumpEnd = jumpingEl.getBoundingClientRect().top;

      trainRef.current.style.transform = `translateY(${
        lastSyncRef.current.y - jumpEnd + jumpStart
      }px)`;

      if (containerRef.current.scrollTop < 50) {
        y = 0;
        trainRef.current.style.transform = `translateY(${0})`;
        lastSyncRef.current.y = y;
      }

      window.TweenLite.to(trainRef.current, 0.5, {
        y: y,
        overwrite: 'all'
      });

      lastSyncRef.current.y = y;
    } catch (e) {
      // console.log(e);
    }
  };

  const syncOnScroll = React.useCallback(() => {
    try {
      const builderFields = [
        ...pane1Ref.current.querySelectorAll('[data-rbd-draggable-id]')
      ];

      const topsOfFields = builderFields.map(
        one => one.getBoundingClientRect().y
      );
      const paneTop = pane2Ref.current.offsetTop;
      let topShowingFieldIndex = 0;
      while (
        topShowingFieldIndex < topsOfFields.length &&
        topsOfFields[topShowingFieldIndex] < paneTop
      ) {
        topShowingFieldIndex++;
      }
      const topOfTopShowingField = topsOfFields[topShowingFieldIndex];
      const windOffset = window.pageYOffset;

      const offset =
        windOffset + topOfTopShowingField - pane2Ref.current.offsetTop - 90;

      syncPreview(builderFields[topShowingFieldIndex], offset);
    } catch (e) {
      console.error(e);
    }
  });

  const debouncedSyncOnScroll = React.useCallback(
    debounce(syncOnScroll, 50, { leading: true, trailing: true }),
    [syncOnScroll]
  );

  React.useEffect(() => {
    if (container) {
      container.addEventListener('scroll', syncOnScroll);
      container.addEventListener('resize', syncOnScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', syncOnScroll);
        container.removeEventListener('resize', syncOnScroll);
      }
    };
  }, [container, syncOnScroll]);

  const VLRef = React.useRef(null);

  const { current: measureCache } = React.useRef(
    new CellMeasurerCache({
      defaultHeight: 100,
      minHeight: 75,
      fixedWidth: true
    })
  );

  const resizeFromItem = index => {
    if (measureCache) {
      measureCache.clear(index);
    }

    if (VLRef.current) {
      debouncedRecomputeVLRefHeights(index, VLRef.current);
    }
  };

  const successCallback = (attribute, newValue) => {
    let callbackMessage = '';
    if (attribute === 'adminonly') {
      if (newValue) {
        callbackMessage = 'Fields have been set as admin only.';
      } else {
        callbackMessage = 'Fields are no longer admin-only.';
      }
    } else if (attribute === 'required') {
      if (newValue) {
        callbackMessage = 'Fields have been marked as required.';
      } else {
        callbackMessage = 'Fields are no longer required.';
      }
    }
    openSnackbar('success', callbackMessage);
  };

  const changeSelectedFields = (attribute, value) => {
    let selectedFieldIds = [];
    const multiSelectedKeys = Object.keys(multiSelected);
    multiSelectedKeys.forEach(one => {
      if (multiSelected[one]) {
        selectedFieldIds.push(one.replace('formbuilder-field-', ''));
      }
    });
    switch (attribute) {
      case 'adminonly':
      case 'required':
        if (!selectedFieldIds.length) {
          return;
        }
        autosave({
          type:
            attribute === 'adminonly' ? MULTI_UPDATE_FIELD_ACCESS : MULTISET,
          payload: {
            id: formId,
            fieldIsToSet: selectedFieldIds,
            attribute,
            value
          },
          callback: () => {
            successCallback(attribute, value);
          }
        });
        // setTimeout(() => {
        //   successCallback(attribute, value);
        // }, 500);
        break;
      default:
        break;
    }
  };

  const dismissSelection = () => {
    setMultiSelected({});
    setShowMultiControls(0);
  };

  const [multiSelected, setMultiSelected] = useState({});

  useEffect(() => {
    const adminIds = adminFields.map(one => one.internalId);
    const copyOfMultiSelected = { ...multiSelected };
    const keys = Object.keys(copyOfMultiSelected);
    if (
      keys.every(one => {
        let fieldId = one.replace('formbuilder-field-', '');
        return !copyOfMultiSelected[one] || adminIds.includes(fieldId);
      })
    ) {
      setSelectedAreAllAdmin(true);
    } else {
      setSelectedAreAllAdmin(false);
    }
  }, [adminFields, multiSelected]);

  const changeMultiSelected = (index, value) => {
    if (index === -1) {
      dismissSelection();
      return;
    }
    let allSelectedAreNew = true;
    let copyOfMultiSelected = { ...multiSelected };
    copyOfMultiSelected[index] = value;
    const keys = Object.keys(copyOfMultiSelected);
    keys.every(one => {
      let fieldId = one.replace('formbuilder-field-', '');
      if (
        copyOfMultiSelected[one] &&
        listOfNewFieldIdsRef.current.indexOf(fieldId) === -1
      ) {
        allSelectedAreNew = false;
        return false;
      }
      return true;
    });
    setCanChangeAllToAdmin(!hasEntries || allSelectedAreNew);
    setMultiSelected(copyOfMultiSelected);
  };

  const numberCurrentlyMultiSelected = Object.values(multiSelected).filter(
    one => one
  ).length;

  return (
    <FormBuilderEditContext.Provider
      value={{
        fields,
        adminFields,
        allFields,
        allFieldsWithSubFields,
        automations: form.automations,
        autosave,
        formId,
        rules,
        setRules,
        reminders,
        setReminders,
        allLookupDependencies,
        hasEntries,
        deletedField,
        setDeletedField,
        // heightsRef,
        resizeFromItem,
        oldFields: allFieldsRef.current,
        sharingType: form.sharingType,
        activeIntegrations,
        activeLicenses,
        anyFieldSelected: numberCurrentlyMultiSelected,
        multiSelected,
        changeMultiSelected
      }}
    >
      <Container
        id="form-builder-container"
        ref={containerRef}
        css={css`
          overflow-y: scroll !important;
          height: calc(100vh - 112px);
        `}
        onScroll={e => {
          if (e.target.id === 'form-builder-container') {
            setIsScroll2TopBtnHidden(e.target.scrollTop <= 100);
          }
          // console.log(`e.target.scrollTop`, e.target.scrollTop);
          // console.log(`e.target`, e.target);
        }}
      >
        <BuilderTitle $useOldFB={useOldFB}>
          <span style={{ flex: 1 }}>
            <InputBase
              id="enduserform-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={() => {
                updateFormTitle();
                setIsTitleInFocus(false);
              }}
              inputRef={titleRef}
              customCSS="[class*='MuiInputBase-root'] {max-height: 50px;} input {font-size: 30px !important; font-weight: 500;}"
              onFocus={() => setIsTitleInFocus(true)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Animation in={isTitleInFocus || isTitleHovered}>
                      <CharacterCounter string={title} maxLength={100} />
                    </Animation>
                  </InputAdornment>
                )
              }}
              inputProps={{
                'data-lpignore': 'true',
                maxLength: 100
              }}
              onMouseEnter={() => setIsTitleHovered(true)}
              onMouseLeave={() => setIsTitleHovered(false)}
            />
            {/* <EditableTitle
              id="enduserform-title"
              content={title}
              setContent={setTitle}
              onBlur={() => {
                updateFormTitle();
                setIsTitleInFocus(false);
              }}
              inputRef={titleRef}
              customCSS="input {font-size: 30px !important;}"
              inputProps={{ maxLength: 100 }}
              onFocus={() => setIsTitleInFocus(true)}
              endAdornment={
                <InputAdornment position="end">
                  <Animation in={isTitleInFocus || isTitleHovered}>
                    <CharacterCounter string={title} maxLength={100} />
                  </Animation>
                </InputAdornment>
              }
              onMouseEnter={() => setIsTitleHovered(true)}
              onMouseLeave={() => setIsTitleHovered(false)}
            /> */}
            <TitleButtonsDiv>
              <Tooltip arrow title="Edit description">
                <IconButton
                  size="small"
                  data-testid="open-overview-dialog"
                  onClick={() => setIsOverviewDialogOpen(true)}
                >
                  <CreateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip arrow title="Insert emoji">
                <IconButton
                  size="small"
                  data-testid="title-emoji-dialog"
                  onClick={() => setShowEmojiDialog(true)}
                >
                  <EmojiIcon />
                </IconButton>
              </Tooltip>
            </TitleButtonsDiv>
          </span>
          <Scroll2TopDiv>
            <Animation in={!isScroll2TopBtnHidden}>
              <Scroll2TopButton containerRef={containerRef.current} />
            </Animation>
          </Scroll2TopDiv>
          <ReorderSectionsLinkContainer>
            <strong>
              {t('reorder_sections.count.text', {
                fieldCount: allFields.length
              })}
            </strong>{' '}
            {showReorderSectionsLink ? (
              <>
                |&nbsp;
                <ReorderSectionsLink
                  onClick={() => setIsReorderSectionsDialogOpen(true)}
                >
                  Re-order sections
                </ReorderSectionsLink>
              </>
            ) : (
              ''
            )}
            {numberCurrentlyMultiSelected ? (
              <>
                &nbsp;|&nbsp;
                <SelectAllLink onClick={toggleSelectAllFields}>
                  {numberCurrentlyMultiSelected === allFields.length
                    ? 'Deselect all'
                    : 'Select all'}
                </SelectAllLink>
              </>
            ) : (
              ''
            )}
          </ReorderSectionsLinkContainer>
        </BuilderTitle>

        <EmojiInserterEngine
          emojiCallback={v => {
            setTitle(v);
            updateFormTitle(v);
          }}
          textInput={titleRef.current}
          showEmojiDialog={showEmojiDialog}
          setShowEmojiDialog={setShowEmojiDialog}
        />
        <ReorderSectionsDialogEngine
          form={form}
          isReorderSectionsDialogOpen={isReorderSectionsDialogOpen}
          setIsReorderSectionsDialogOpen={setIsReorderSectionsDialogOpen}
          autosave={autosave}
        />
        <FormBuilderMultiControlsEngine
          changeSelectedFields={changeSelectedFields}
          dismiss={dismissSelection}
          selectedCount={numberCurrentlyMultiSelected}
          canChangeAllToAdmin={canChangeAllToAdmin}
          selectedAreAllAdmin={selectedAreAllAdmin}
        />

        <SplitPane>
          <Pane
            css={css`
              @media (max-width: 800px) {
                width: 100%;
              }
            `}
            ref={pane1Ref}
            // onFocus={e => {
            //   const builderField = e.target.closest('[data-rbd-draggable-id]');

            //   if (builderField) {
            //     // syncPreview(builderField);
            //   }
            // }}
          >
            <div>
              {allFields && (
                <DragDropContext
                  onDragEnd={result => {
                    window.isDragging = false;
                    // console.log('DND end');
                    document.body.classList.remove('-is-dragging');
                    document.body.classList.add('-is-not-dragging');

                    autosave({
                      type: REORDER_FIELDS,
                      payload: {
                        visibleFields: allFields,
                        allFields: allFieldsWithSubFields,
                        ...result
                      }
                    });
                    setMultiSelected({});
                    setShowMultiControls(0);
                  }}
                  onDragStart={() => {
                    // console.log('DND start');
                    window.isDragging = true;
                    document.body.classList.remove('-is-not-dragging');
                    document.body.classList.add('-is-dragging');
                  }}
                >
                  <Droppable
                    droppableId="droppable"
                    mode="virtual"
                    renderClone={(provided, snapshot, rubric) => (
                      <CellMeasurer
                        rowIndex={rubric.source.index}
                        columnIndex={0}
                        cache={measureCache}
                      >
                        {({ registerChild }) => (
                          <div
                            ref={registerChild}
                            data-testid="formbuilder-edit-fields"
                            id="builder-fields-wrapper"
                          >
                            <Item
                              provided={provided}
                              snapshot={snapshot}
                              item={allFields[rubric.source.index]}
                              index={rubric.source.index}
                            />
                          </div>
                        )}
                      </CellMeasurer>
                    )}
                  >
                    {droppableProvided => (
                      <WindowScroller scrollElement={containerRef.current}>
                        {({
                          height,
                          isScrolling,
                          onChildScroll,
                          scrollTop,
                          registerChild
                        }) => (
                          <div ref={registerChild}>
                            <List
                              autoHeight
                              rowCount={allFields.length}
                              height={height}
                              isScrolling={isScrolling}
                              onScroll={onChildScroll}
                              scrollTop={scrollTop}
                              deferredMeasurementCache={measureCache}
                              rowHeight={measureCache.rowHeight}
                              estimatedRowSize={200}
                              width={560}
                              overscanRowCount={2}
                              ref={ref => {
                                // react-virtualized has no way to get the list's ref that I can so
                                // So we use the `ReactDOM.findDOMNode(ref)` escape hatch to get the ref
                                if (ref) {
                                  // eslint-disable-next-line react/no-find-dom-node
                                  const whatHasMyLifeComeTo =
                                    ReactDOM.findDOMNode(ref);
                                  if (
                                    whatHasMyLifeComeTo instanceof HTMLElement
                                  ) {
                                    droppableProvided.innerRef(
                                      whatHasMyLifeComeTo
                                    );
                                  }

                                  VLRef.current = ref;
                                }
                              }}
                              rowRenderer={getRowRender(
                                allFields,
                                hasEntries,
                                measureCache,
                                listOfNewFieldIdsRef,
                                setShowMultiControls,
                                setCanChangeAllToAdmin
                              )}
                            />
                          </div>
                        )}
                      </WindowScroller>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
            <AddQuestionButton
              activeIntegrations={activeIntegrations}
              activeLicenses={activeLicenses}
              autosave={autosave}
              sharingType={form.sharingType}
              style={{ marginBottom: '80vh', height: 100 }}
            />
          </Pane>
          <Pane
            css={css`
              pointer-events: none;
              @media (max-width: 800px) {
                display: none;
              }
              ${useOldFB &&
              `position: absolute;
              left: ${leftPanePercent}%;`}
            `}
            ref={pane2Ref}
          >
            <PreviewWrapper data-testid="formbuilder-preview-form">
              <div
                ref={trackRef}
                css={css`
                  position: fixed;
                  width: 560px;
                  top: ${useOldFB ? '50%' : `${topGap}px`};
                  ${!useOldFB ? 'background: #fff;' : ''}
                `}
              >
                <div
                  id="preview-pane"
                  ref={trainRef}
                  css={css`
                    ${!useOldFB ? 'background: #fff;' : ''}
                    padding: 12px 48px;
                    ${!useOldFB
                      ? 'max-height: calc(100vh - 190px); overflow-y: hidden;'
                      : 'pointer-events: none;'}
                  `}
                >
                  <Collapse in={allFields.length > 0} appear>
                    {!useOldFB
                      ? !!showPreview && (
                          <FormPreviewEngine
                            form={form}
                            customCSS={'form {opacity: 1 !important;}'}
                          />
                        )
                      : 'Preview is unavailable for forms exceeding 1,000 fields'}
                  </Collapse>
                  {allFields.length === 0 && emptyState}
                </div>
              </div>
            </PreviewWrapper>
          </Pane>
        </SplitPane>
      </Container>
    </FormBuilderEditContext.Provider>
  );
};

export default FormBuilderEdit;
